import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNotification } from '../common';

/**
 * VideoCoursesManager component for administrators to manage video content
 * Allows adding, editing, and removing video sources for courses
 */
const VideoCoursesManager = () => {
  const { showNotification } = useNotification();
  
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [videoSources, setVideoSources] = useState([]);
  const [newVideoSource, setNewVideoSource] = useState({
    title: '',
    description: '',
    url: '',
    durationMinutes: 0,
    durationSeconds: 0
  });
  
  // State for editing existing videos
  const [editingVideoId, setEditingVideoId] = useState(null);
  const [editingVideoData, setEditingVideoData] = useState({
    title: '',
    description: '',
    url: '',
    durationMinutes: 0,
    durationSeconds: 0
  });
  
  // Fetch all courses that can have video content
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('token');
        if (!token) {
          setError('Authentication token not found');
          setLoading(false);
          return;
        }
        
        const config = {
          headers: {
            'x-auth-token': token
          }
        };
        
        // Fetch all courses (not just video courses)
        const response = await axios.get('/api/settings/courses', config);
        
        // Filter for courses that have 'Video' format or already have video content
        const videoCourses = response.data.filter(
          course => course.course_format === 'Video' || course.video_content
        );
        
        setCourses(videoCourses);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching courses:', err);
        setError('Failed to load courses: ' + (err.response?.data?.message || err.message));
        setLoading(false);
      }
    };
    
    fetchCourses();
  }, []);
  
  // When a course is selected, load its video sources
  useEffect(() => {
    if (selectedCourse) {
      setVideoSources(selectedCourse.video_sources || []);
    } else {
      setVideoSources([]);
    }
  }, [selectedCourse]);
  
  const handleCourseSelect = (course) => {
    setSelectedCourse(course);
  };
  
  const handleVideoInputChange = (e) => {
    const { name, value } = e.target;
    setNewVideoSource({
      ...newVideoSource,
      [name]: value
    });
  };
  
  const handleAddVideo = () => {
    // Validate required fields
    if (!newVideoSource.title || !newVideoSource.url) {
      showNotification('Video title and URL are required', 'error');
      return;
    }
    
    // Create a unique ID for the video
    const videoId = `video-${Date.now()}`;
    
    // Calculate total duration in seconds
    const totalDurationInSeconds = 
      (parseInt(newVideoSource.durationMinutes) || 0) * 60 + 
      (parseInt(newVideoSource.durationSeconds) || 0);
    
    // Add the new video to the list
    const updatedSources = [
      ...videoSources,
      {
        ...newVideoSource,
        duration: totalDurationInSeconds,
        id: videoId
      }
    ];
    
    setVideoSources(updatedSources);
    
    // Reset the form
    setNewVideoSource({
      title: '',
      description: '',
      url: '',
      durationMinutes: 0,
      durationSeconds: 0
    });
  };
  
  const handleRemoveVideo = (videoId) => {
    const updatedSources = videoSources.filter(source => source.id !== videoId);
    setVideoSources(updatedSources);
  };
  
  // Move a video up in the list (decrease its index)
  const handleMoveUp = (index) => {
    if (index === 0) return; // Already at the top
    
    const updatedSources = [...videoSources];
    const temp = updatedSources[index];
    updatedSources[index] = updatedSources[index - 1];
    updatedSources[index - 1] = temp;
    
    setVideoSources(updatedSources);
  };
  
  // Move a video down in the list (increase its index)
  const handleMoveDown = (index) => {
    if (index === videoSources.length - 1) return; // Already at the bottom
    
    const updatedSources = [...videoSources];
    const temp = updatedSources[index];
    updatedSources[index] = updatedSources[index + 1];
    updatedSources[index + 1] = temp;
    
    setVideoSources(updatedSources);
  };
  
  // Start editing a video
  const handleEditVideo = (videoId) => {
    const videoToEdit = videoSources.find(source => source.id === videoId);
    if (videoToEdit) {
      // Convert total seconds to minutes and seconds
      const totalSeconds = parseInt(videoToEdit.duration) || 0;
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      
      setEditingVideoData({
        ...videoToEdit,
        durationMinutes: minutes,
        durationSeconds: seconds
      });
      setEditingVideoId(videoId);
    }
  };
  
  // Handle input changes for editing
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditingVideoData({
      ...editingVideoData,
      [name]: value
    });
  };
  
  // Cancel editing
  const handleCancelEdit = () => {
    setEditingVideoId(null);
    setEditingVideoData({
      title: '',
      description: '',
      url: '',
      durationMinutes: 0,
      durationSeconds: 0
    });
  };
  
  // Save edited video
  const handleSaveEdit = () => {
    // Validate required fields
    if (!editingVideoData.title || !editingVideoData.url) {
      showNotification('Video title and URL are required', 'error');
      return;
    }
    
    // Calculate total duration in seconds
    const totalDurationInSeconds = 
      (parseInt(editingVideoData.durationMinutes) || 0) * 60 + 
      (parseInt(editingVideoData.durationSeconds) || 0);
    
    // Update the video in the list
    const updatedSources = videoSources.map(source => {
      if (source.id === editingVideoId) {
        return {
          ...editingVideoData,
          duration: totalDurationInSeconds,
          id: editingVideoId
        };
      }
      return source;
    });
    
    setVideoSources(updatedSources);
    
    // Reset editing state
    setEditingVideoId(null);
    setEditingVideoData({
      title: '',
      description: '',
      url: '',
      durationMinutes: 0,
      durationSeconds: 0
    });
    
    showNotification('Video updated successfully', 'success');
  };
  
  const handleSaveCourse = async () => {
    try {
      if (!selectedCourse) {
        showNotification('No course selected', 'error');
        return;
      }
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found');
        return;
      }
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      // Calculate total duration
      const totalDuration = videoSources.reduce(
        (total, source) => total + (parseInt(source.duration) || 0),
        0
      );
      
      // Prepare data for the API
      const videoData = {
        video_sources: videoSources,
        content_duration: totalDuration
      };
      
      // Make API call to update course video content
      const response = await axios.put(
        `/api/video-courses/${selectedCourse.id}`,
        videoData,
        config
      );
      
      const updatedCourse = {
        ...selectedCourse,
        video_content: true,
        video_sources: videoSources,
        content_duration: totalDuration
      };
      
      // Update the local state
      setCourses(prevCourses => 
        prevCourses.map(course => 
          course.id === selectedCourse.id ? updatedCourse : course
        )
      );
      
      setSelectedCourse(updatedCourse);
      
      showNotification('Course video content saved successfully', 'success');
    } catch (err) {
      console.error('Error saving course video content:', err);
      showNotification(
        'Failed to save course video content: ' + (err.response?.data?.message || err.message),
        'error'
      );
    }
  };
  
  if (loading) {
    return <div className="text-center p-8">Loading courses...</div>;
  }
  
  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
        {error}
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-semibold mb-6">Manage Video Courses</h2>
      
      {courses.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p className="mb-4">No courses with video format found.</p>
          <p className="text-sm">
            Create a course with "Video" format in Course Settings first.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Course List Sidebar */}
          <div className="md:col-span-3 bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-medium mb-4 border-b pb-2">Video Courses</h3>
            
            <ul className="space-y-2">
              {courses.map(course => (
                <li key={course.id}>
                  <button
                    onClick={() => handleCourseSelect(course)}
                    className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                      selectedCourse && selectedCourse.id === course.id
                        ? 'bg-blue-100 text-blue-700'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <div className="font-medium">{course.name}</div>
                    <div className="text-xs">
                      {course.video_content ? (
                        <span className="text-green-600">
                          {course.video_sources?.length || 0} videos
                        </span>
                      ) : (
                        <span className="text-gray-500">No videos yet</span>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Course Video Editor */}
          <div className="md:col-span-9">
            {selectedCourse ? (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium">
                    {selectedCourse.name} - Video Content
                  </h3>
                  <button
                    onClick={handleSaveCourse}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Save Changes
                  </button>
                </div>
                
                {/* Current Videos */}
                <div className="mb-6">
                  <h4 className="text-md font-medium mb-3">Current Videos</h4>
                  
                  {videoSources.length === 0 ? (
                    <div className="text-center py-4 bg-gray-50 rounded-lg border">
                      <p className="text-gray-500">No videos added yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {videoSources.map((source, index) => (
                        <div 
                          key={source.id || index} 
                          className="bg-gray-50 p-3 rounded-lg"
                        >
                          {editingVideoId === source.id ? (
                            // Edit Mode
                            <div className="space-y-3">
                              <div>
                                <label className="block text-gray-700 text-sm font-bold mb-1">
                                  Title
                                </label>
                                <input
                                  type="text"
                                  name="title"
                                  value={editingVideoData.title}
                                  onChange={handleEditInputChange}
                                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                  placeholder="Video title"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-gray-700 text-sm font-bold mb-1">
                                  Description
                                </label>
                                <textarea
                                  name="description"
                                  value={editingVideoData.description}
                                  onChange={handleEditInputChange}
                                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                  placeholder="Brief description of this video"
                                  rows="2"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-gray-700 text-sm font-bold mb-1">
                                  Google Drive URL
                                </label>
                                <input
                                  type="text"
                                  name="url"
                                  value={editingVideoData.url}
                                  onChange={handleEditInputChange}
                                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                  placeholder="https://drive.google.com/file/d/..."
                                />
                              </div>
                              
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-gray-700 text-sm font-bold mb-1">
                                    Duration (minutes)
                                  </label>
                                  <input
                                    type="number"
                                    name="durationMinutes"
                                    value={editingVideoData.durationMinutes}
                                    onChange={handleEditInputChange}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                    placeholder="Minutes"
                                    min="0"
                                  />
                                </div>
                                <div>
                                  <label className="block text-gray-700 text-sm font-bold mb-1">
                                    Duration (seconds)
                                  </label>
                                  <input
                                    type="number"
                                    name="durationSeconds"
                                    value={editingVideoData.durationSeconds}
                                    onChange={handleEditInputChange}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                    placeholder="Seconds"
                                    min="0"
                                    max="59"
                                  />
                                </div>
                              </div>
                              
                              <div className="flex justify-end space-x-2 pt-2">
                                <button
                                  onClick={handleCancelEdit}
                                  className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={handleSaveEdit}
                                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                                >
                                  Save Changes
                                </button>
                              </div>
                            </div>
                          ) : (
                            // View Mode
                            <div className="flex justify-between items-center">
                              <div className="flex-1">
                                <div className="font-medium">{source.title}</div>
                                {source.description && (
                                  <div className="text-sm text-gray-600">{source.description}</div>
                                )}
                                <div className="text-xs text-gray-500 mt-1">
                                  <a 
                                    href={source.url} 
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline"
                                  >
                                    {source.url}
                                  </a>
                                  {source.duration > 0 && (
                                    <span className="ml-2">
                                      Duration: {Math.floor(source.duration / 60)}:{(source.duration % 60).toString().padStart(2, '0')}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <div className="flex flex-col mr-2">
                                  <button
                                    onClick={() => handleMoveUp(index)}
                                    disabled={index === 0}
                                    className={`text-gray-600 ${index === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:text-gray-800'}`}
                                    title="Move Up"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => handleMoveDown(index)}
                                    disabled={index === videoSources.length - 1}
                                    className={`text-gray-600 ${index === videoSources.length - 1 ? 'opacity-30 cursor-not-allowed' : 'hover:text-gray-800'}`}
                                    title="Move Down"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  </button>
                                </div>
                                <button
                                  onClick={() => handleEditVideo(source.id)}
                                  className="text-blue-600 hover:text-blue-800"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleRemoveVideo(source.id)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Add New Video Form */}
                <div className="bg-gray-50 p-4 rounded-lg border">
                  <h4 className="text-md font-medium mb-3">Add New Video</h4>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-1">
                        Title
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={newVideoSource.title}
                        onChange={handleVideoInputChange}
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        placeholder="Video title"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-1">
                        Description
                      </label>
                      <textarea
                        name="description"
                        value={newVideoSource.description}
                        onChange={handleVideoInputChange}
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        placeholder="Brief description of this video"
                        rows="2"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-1">
                        Google Drive URL
                      </label>
                      <input
                        type="text"
                        name="url"
                        value={newVideoSource.url}
                        onChange={handleVideoInputChange}
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        placeholder="https://drive.google.com/file/d/..."
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Paste the full URL to the Google Drive video file
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-1">
                          Duration (minutes)
                        </label>
                        <input
                          type="number"
                          name="durationMinutes"
                          value={newVideoSource.durationMinutes}
                          onChange={handleVideoInputChange}
                          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                          placeholder="Minutes"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-1">
                          Duration (seconds)
                        </label>
                        <input
                          type="number"
                          name="durationSeconds"
                          value={newVideoSource.durationSeconds}
                          onChange={handleVideoInputChange}
                          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                          placeholder="Seconds"
                          min="0"
                          max="59"
                        />
                      </div>
                    </div>
                    
                    <div className="pt-2">
                      <button
                        onClick={handleAddVideo}
                        className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
                      >
                        Add Video
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-10 bg-gray-50 rounded-lg border">
                <h3 className="text-lg font-medium text-gray-700 mb-2">
                  Select a Course
                </h3>
                <p className="text-gray-500">
                  Choose a course from the list to manage its video content.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoCoursesManager;